/**
 * Where each email draft shows in a conversation. The agent revises a draft in place (`drafts.update` keeps its
 * id), so the same draft comes back in turn after turn; only its latest appearance stays, and the earlier cards
 * give way to it. Each place that would show a draft claims it with its position in the conversation (the row's
 * index); a claim with a later one supersedes it.
 *
 * Claims are made from the data (a changeset's ops, an action's target), never from a mounted card, so hiding a
 * superseded card can't drop its claim and bring it back.
 */
import { inject, provide, reactive, toRaw, type InjectionKey } from 'vue'

export interface DraftClaims {
    /** Claim draft `id` at conversation position `order`; returns the release. */
    claim(id: string, order: number): () => void
    /** A later place in the conversation shows draft `id`. */
    superseded(id: string, order: number): boolean
}

const KEY: InjectionKey<DraftClaims> = Symbol('agentDraftClaims')

export function createDraftClaims(): DraftClaims {
    const claims = reactive(new Map<string, number[]>())
    // Claims are made inside effects: they read the map untracked, so claiming never re-runs the effect that claims.
    const raw = toRaw(claims)
    return {
        claim(id, order) {
            claims.set(id, [...(raw.get(id) ?? []), order])
            return () => {
                const list = raw.get(id)
                if (!list) return
                const at = list.indexOf(order)
                if (at < 0) return
                const next = list.filter((_, i) => i !== at)
                if (next.length) claims.set(id, next)
                else claims.delete(id)
            }
        },
        superseded(id, order) {
            const list = claims.get(id)
            return Boolean(list?.some((o) => o > order))
        },
    }
}

export function provideDraftClaims(): DraftClaims {
    const c = createDraftClaims()
    provide(KEY, c)
    return c
}

/** The conversation's claims; null outside one (nothing is ever superseded there). */
export function useDraftClaims(): DraftClaims | null {
    return inject(KEY, null)
}
